import dynamic from 'next/dynamic';

import { useAiCaptionDropdownOptions } from '@/utils/hooks/scheduler/aiCaption/useAiCaptionDropdownOptions';
import type { useAiEditActions } from '@/utils/hooks/scheduler/aiCaption/useAiEditActions';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialProfileType } from '@/types/socialProfileType';

import Icon from '@/components/base/Icon';
import { AiDropdown } from '@/components/composite/CaptionComponent/Components/CaptionFooter/CaptionAi/Components/AiDropdown';
const SchedulerCaptionAIModal = dynamic(
    () =>
        import(
            '@/components/composite/Scheduler/PostFunnel/SchedulerCaption/SchedulerCaptionAIModal'
        )
);
const SchedulerCaptionFromUrlModal = dynamic(() =>
    import(
        '@/components/composite/Scheduler/PostFunnel/SchedulerCaption/SchedulerCaptionFromUrlModal'
    ).then((mod) => mod.SchedulerCaptionFromUrlModal)
);

type AiCaptionProps = {
    isButtonDisabled: boolean;
    updateCaption: (param: string) => void;
    clearError?: () => string;
    currentCaption?: string;
    currentProfileId?: SocialProfileType['id'];
    hasPreviousAction: boolean;
    inSchedulerFunnel: boolean;
    previousCallback: () => void;
    dropdownCallbacks: ReturnType<typeof useAiEditActions>;
};

export function AiCaption({
    isButtonDisabled,
    updateCaption,
    clearError,
    currentCaption,
    hasPreviousAction,
    previousCallback,
    dropdownCallbacks,
    currentProfileId
}: AiCaptionProps) {
    const { t } = useTranslation('scheduler');
    const { isOpen: isOpenGenerateCaptionModal, toggle: toggleGenerateCaptionModal } = useToggle();
    const { isOpen: isOpenGenerateCaptionFromUrlModal, toggle: toggleGenerateCaptionFromUrlModal } =
        useToggle();
    const dropdownOptions = useAiCaptionDropdownOptions({
        toggleGenerateCaptionModal,
        toggleGenerateCaptionFromUrlModal,
        handleEnhanceCaption: dropdownCallbacks.handleEnhanceCaption,
        handleHashtags: dropdownCallbacks.handleHashtags,
        handleToneOfVoiceCaption: dropdownCallbacks.handleToneOfVoiceCaption,
        handleTranslateCaption: dropdownCallbacks.handleTranslateCaption,
        isOptionDisabled: !currentCaption?.trim()?.length
    });

    return (
        <>
            <div className="z-15 mb-3 ml-auto flex items-center gap-5">
                {hasPreviousAction ? (
                    <button
                        disabled={isButtonDisabled}
                        onClick={previousCallback}
                        className="flex items-center gap-2 text-sm text-icoBlue focus:ring-0 focus:ring-offset-0 disabled:text-icoGray-500 disabled:dark:text-icoDarkMode-wolf">
                        <Icon icon="revert" />
                        <span>{t('commons:previous')}</span>
                    </button>
                ) : null}

                <AiDropdown
                    dropdownOptions={dropdownOptions}
                    position="base"
                    disabled={isButtonDisabled}
                />
            </div>
            <SchedulerCaptionAIModal
                updateCaption={updateCaption}
                clearError={clearError}
                currentCaption={currentCaption}
                open={isOpenGenerateCaptionModal}
                onDismiss={toggleGenerateCaptionModal}
            />
            <SchedulerCaptionFromUrlModal
                currentProfileId={currentProfileId}
                updateCaption={updateCaption}
                clearError={clearError}
                currentCaption={currentCaption}
                open={isOpenGenerateCaptionFromUrlModal}
                onDismiss={toggleGenerateCaptionFromUrlModal}
            />
        </>
    );
}
